import { format } from "date-fns";
import React, { useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { Button, Dropdown } from "react-bootstrap";
import { FaPencilAlt } from "react-icons/fa";
import { getUploadedFile } from "requesters/uploadsRequester";
import { getCurrentRole } from "../../../reducers";
import EditFileModal from "../modals/EditFileModal";
import RenameFileModal from "../modals/RenameFileModal";
import SendReleasedFilesEmailModal from "../modals/SendReleasedFilesEmailModal";
import DeleteFile from "./DeleteFile";
import SupersededFile from "./SupersededFile";
import { RECEIVED_PLANS_CATEGORY, PDF_MARKUP_ALLOWED_ROLES, FILE_GROUPS, ADMIN } from "../../../../../utils/constants";
import MarkFileAsUsedPlansForCalc from "./MarkFileAsUsedPlansForCalc";
import FileIcon from "./fileRow/FileIcon";
import PdfViewer from "../../../components/PdfViewer/PdfViewer";

export default function FileRow({
  file,
  fileGroup,
  fileGroupKey,
  jobService,
  onPdfExport,
  fileOptions = [],
  selectedFileIds = [],
  getCheckedFiles = () => {},
  reloadData = () => {},
  isDeleted = false,
  canUpdate = false,
  showSuperseded = false
}) {
  const {
    attributes: {
      createdByUser,
      childJobCode,
      fileTrackedUrl,
      fileName,
      documentCategory,
      isPlansUsedForCalc,
      updatedAt,
      name,
      discardedAt,
      discardedByUser,
      superseded,
      isExpired,
      extractMsg,
      readyForRelease
    }
  } = file.data

  const currentTeams = useSelector(getCurrentRole)
  const isAdmin = currentTeams.some(team => team.includes(ADMIN))
  const [showEditFiletModal, setShowEditFiletModal] = useState(false)
  const [showPdfViewer, setShowPdfViewer] = useState(false)
  const [showRenameFileModal, setShowRenameFileModal] = useState(false)
  const isJobInstructions = fileGroup === "Job Instructions"
  const isReadyForRelease = fileGroup === "Ready for Release"
  const isReleasedFiles = fileGroup === "Released Files"
  const isSearches = fileGroup === "Searches"
  const isPaidFull = jobService?.attributes?.invoiceState === "paid_full" || Number(jobService?.attributes?.cost) === 0
  const isAccounts = !(jobService?.fifty_fifty || jobService?.prior_to_release)
  const outstandingInvoiceFlagged = jobService?.attributes?.outstandingInvoiceFlagged
  const unmatchedInvoiceFlagged = jobService?.attributes?.unmatchedInvoiceFlagged
  const [showReleasedFilesModal, setShowReleasedFilesModal] = useState(false)
  const [loadingFileUrl, setLoadingFileUrl] = useState(false)
  const EXTRACT_MESSAGE = {
    extracting: {
      text: "Extracting file",
      color: "text-green"
    },
    error: {
      text: "Error extracting file",
      color: "text-danger"
    },
    unsupported: {
      text: "Unsupported file",
      color: "text-danger"
    }
  }
  const FILE_GROUP_LABEL_TO_KEY = {
    "Released Files": "released_files",
    "Ready for Release": "ready_for_release",
    "Registered Surveyor Files": "registered_surveyor_files",
    "Drafting Files": "drafting_files",
    "Flag Files": "flag_files",
    "Surveyor Files": "surveyor_files",
    "Job Instructions": "job_instructions",
    "Deleted Files": "deleted_files",
    Searches: "searches",
    Photos: "photos"
  }

  const canShowQuoteAcceptanceFiles = ["admin", "accounts", "operations", "3d_admin"].some(team => currentTeams.includes(team))
  if (isJobInstructions && !canShowQuoteAcceptanceFiles) {
    _.remove(fileOptions, o => o === "Quote Acceptance" || o === "Quote");
  }

  function attachAndDetachDownloadLink(downloadLink) {
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  }

  // Check if file is PDF
  const isPdfFile = () => {
    const extension = fileName?.split(".").pop()?.toLowerCase()
    return extension === "pdf"
  }

  // Check if user has permission to use PDF markup feature for this specific file
  // Users can only markup PDFs from file categories covered by their role
  const canUserMarkupPdf = () => {
    // First check if user has a role that allows PDF markup
    const hasMarkupRole = PDF_MARKUP_ALLOWED_ROLES.some(role => currentTeams.some(team => team.includes(role)))
    if (!hasMarkupRole) return false

    // Admin/Operations can markup any file
    if (currentTeams.some(team => team.includes("admin") || team.includes("operations"))) {
      return true
    }

    // RS roles can markup RS files AND drafting files (they review drafting work before signing)
    if (currentTeams.some(team => team.includes("registered_surveyors"))) {
      if (FILE_GROUPS.registered_surveyor_files.includes(documentCategory)
          || FILE_GROUPS.drafting_files.includes(documentCategory)
          || FILE_GROUPS.vr_drafting_files.includes(documentCategory)) {
        return true
      }
    }

    // Drafting roles can only markup Drafting files
    if (currentTeams.some(team => team.includes("drafting") || team.includes("overseas_drafting"))) {
      if (FILE_GROUPS.drafting_files.includes(documentCategory)
          || FILE_GROUPS.vr_drafting_files.includes(documentCategory)) {
        return true
      }
    }

    return false
  }

  const showPdfMarkup = isPdfFile() && canUserMarkupPdf()
  const showAdminPdfDropdown = isAdmin && showPdfMarkup
  const showAdminRenameOnly = isAdmin && !showPdfMarkup

  // Direct download function (bypasses PDF viewer)
  async function handleDirectDownload() {
    setLoadingFileUrl(true)
    const result = await getUploadedFile(fileTrackedUrl)
    if ("response" in result) {
      const extension = result?.response?.json?.extension || ""
      const download_url = result?.response?.json?.download_url
      const file_name = result?.response?.json?.file_name
      const downloadLink = document.createElement("a");
      const isUrlFile = extension && extension.toLowerCase() === "url"

      if (isUrlFile) {
        fetch(download_url)
          .then(data => data.text())
          .then(content => {
            const lines = content?.split("\n") || []
            const urlLine = lines.find(l => l.toLowerCase().includes("url="))
            const url = urlLine?.split("=")?.pop()
            downloadLink.href = url;
            downloadLink.setAttribute("target", "_blank")
            attachAndDetachDownloadLink(downloadLink)
            setLoadingFileUrl(false)
          })
      } else if (extension && extension.toLowerCase() === "xml") {
        fetch(download_url)
          .then(data => data.blob())
          .then(blob => {
            const url = window.URL.createObjectURL(new Blob([blob]));
            downloadLink.href = url;
            downloadLink.setAttribute("download", file_name)
            attachAndDetachDownloadLink(downloadLink)
            setLoadingFileUrl(false)
          })
          .catch(() => {
            setLoadingFileUrl(false)
          })
      } else {
        downloadLink.href = download_url;
        downloadLink.setAttribute("download", file_name)
        downloadLink.setAttribute("target", "_blank")
        attachAndDetachDownloadLink(downloadLink)
        setLoadingFileUrl(false)
      }
    } else {
      setLoadingFileUrl(false)
    }
  }

  const renderSendReleasedFilesEmailModal = useCallback(() => (
    <SendReleasedFilesEmailModal
      show={showReleasedFilesModal}
      onHide={() => setShowReleasedFilesModal(false)}
      jobService={jobService}
      file={file} />
  ), [jobService, showReleasedFilesModal, file])

  const isReceivedPlans = isJobInstructions && documentCategory === RECEIVED_PLANS_CATEGORY
  const isUsedPlans = isReceivedPlans && isPlansUsedForCalc

  // RS Checks files are always displayed as superseded (no restore option)
  const isRsChecksFile = documentCategory === "RS Checks"
  const displayAsSuperseded = superseded || isRsChecksFile
  const rsChecksStyle = { opacity: displayAsSuperseded ? 0.5 : 1 }

  return (
    <>
      {isJobInstructions && !fileOptions.includes(documentCategory)
        ? <React.Fragment></React.Fragment>
        : (
          <tr className={`border-top border-bottom ${isSearches && isExpired ? "bg-coral" : ""} ${isUsedPlans ? "hightlighted hl-warning" : ""}`}>
            {(!(isReadyForRelease || isReleasedFiles)) && (
              <td width="2%" className="align-middle">
                {!displayAsSuperseded && (
                  <div className="ml-2 d-flex align-items-center">
                    <div className="custom-control custom-checkbox" key={file.data.id}>
                      <input
                        className="custom-control-input"
                        id={file.data.id}
                        type="checkbox"
                        checked={selectedFileIds.includes(file.data.id)}
                        onChange={() => getCheckedFiles(file)} />
                      <label className="custom-control-label" htmlFor={file.data.id}></label>
                    </div>
                  </div>
                )}
              </td>
            )}
            <td style={{ ...rsChecksStyle, maxWidth: 500 }} className="align-middle">
              <div className="row align-items-center">
                <FileIcon file={file} />
                <div className="col-10">
                  <div className="d-flex align-items-center justify-content-between w-100">
                    <Button
                      disabled={loadingFileUrl}
                      variant="link"
                      onClick={() => handleDirectDownload()}
                      title={name || fileName}
                      className="text-left text-break text-wrap p-0 mr-3 flex-grow-1">
                      {name || fileName}
                    </Button>
                    {showAdminPdfDropdown && (
                      <Dropdown className="ml-2">
                        <Dropdown.Toggle
                          as="button"
                          variant="link"
                          className="btn btn-link p-0 d-flex align-items-center"
                          title="Edit PDF"
                          aria-label="Edit PDF">
                          <FaPencilAlt size={14} color="#007bff" />
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item as="button" onClick={() => setShowPdfViewer(true)}>
                            Edit PDF
                          </Dropdown.Item>
                          <Dropdown.Item
                            as="button"
                            disabled={readyForRelease}
                            onClick={() => setShowRenameFileModal(true)}>
                            Rename File
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    )}
                    {showAdminRenameOnly && (
                      <button
                        type="button"
                        className="btn btn-link p-0 ml-2 d-flex align-items-center"
                        onClick={() => setShowRenameFileModal(true)}
                        disabled={readyForRelease}
                        title="Rename File"
                        aria-label="Rename File">
                        <FaPencilAlt size={14} color="#007bff" />
                      </button>
                    )}
                    {!isAdmin && showPdfMarkup && (
                      <button
                        type="button"
                        className="btn btn-link p-0 ml-2 d-flex align-items-center"
                        onClick={() => setShowPdfViewer(true)}
                        title="Edit PDF"
                        aria-label="Edit PDF">
                        <FaPencilAlt size={14} color="#007bff" />
                      </button>
                    )}
                  </div>
                  {extractMsg && (
                    <i style={{ fontSize: "0.85rem", fontWeight: 600 }} className={EXTRACT_MESSAGE[extractMsg]?.color}>
                      (
                      {EXTRACT_MESSAGE[extractMsg]?.text}
                      )
                    </i>
                  )}
                </div>
              </div>
            </td>
            <td style={{ ...rsChecksStyle, fontSize: "1rem" }} className="text-secondary text-center align-middle">
              {isReceivedPlans ? (
                <div className="d-flex align-items-center justify-content-start">
                  <MarkFileAsUsedPlansForCalc
                    file={file}
                    jobService={jobService}
                    reloadData={reloadData}
                    isUsedPlans={isUsedPlans} />
                  <span className="text-left">
                    {isUsedPlans ? "Plans Used for Calculations" : documentCategory}
                  </span>
                </div>
              ) : (
                <div>
                  <p className="mb-0">
                    {documentCategory}
                    {isRsChecksFile && (
                      <span
                        className="ml-1 text-muted"
                        title="RS Checks files are automatically superseded and cannot be restored"
                        style={{ cursor: "help" }}>
                        (auto-superseded)
                      </span>
                    )}
                  </p>
                  {childJobCode && (
                    <p className="mb-0 font-075rem">
                      {childJobCode}
                      {" "}
                      Job
                    </p>
                  )}
                </div>
              )}
            </td>
            <td style={{ ...rsChecksStyle, fontSize: "1rem" }} className="text-secondary text-center align-middle">{createdByUser}</td>
            <td style={{ ...rsChecksStyle, fontSize: "1rem" }} className="text-center text-secondary align-middle">
              <span className="mr-2">{format(new Date(updatedAt), "dd MMM yyyy, h:mm aa")}</span>
            </td>
            {(isReleasedFiles && (isPaidFull || isAccounts)) && (
              <td className="align-middle" style={{ fontSize: "1rem" }}>
                <button disabled={outstandingInvoiceFlagged || unmatchedInvoiceFlagged} type="button" className="btn btn-link" onClick={() => setShowReleasedFilesModal(true)}>
                  {file.data.attributes.releasedFileSentAt ? "Resend" : "Send to Client"}
                </button>
                {renderSendReleasedFilesEmailModal()}
              </td>
            )}
            {isDeleted && (
              <>
                <td style={{ fontSize: "1rem" }} className="text-secondary text-center align-middle">{discardedByUser}</td>
                <td style={{ fontSize: "1rem" }} className="text-secondary text-center align-middle">{format(new Date(discardedAt), "dd MMM yyyy, h:mm aa")}</td>
              </>
            )}
            {(canUpdate) && (
              <td style={{ fontSize: "1rem" }} className="align-middle">
                {(!isDeleted && !fileGroup) && (
                  <>
                    <button
                      className="btn btn-link btn-lg p-0 pb-1 mr-2"
                      onClick={() => setShowEditFiletModal(true)}
                      type="button">
                      Edit
                    </button>

                    <EditFileModal
                      file={file}
                      fileOptions={fileOptions}
                      show={showEditFiletModal}
                      reloadData={reloadData}
                      onHide={() => { setShowEditFiletModal(false) }} />
                  </>
                )}
                {(!isReleasedFiles || !file.data.attributes.releasedFileSentAt) && (
                  <DeleteFile
                    file={file}
                    reloadData={reloadData} />
                )}
              </td>
            )}
            {showSuperseded && !isRsChecksFile && (
              <td style={{ fontSize: "1rem" }} className="align-middle">
                <SupersededFile
                  reloadData={reloadData}
                  file={file}>
                </SupersededFile>
              </td>
            )}
            {showSuperseded && isRsChecksFile && (
              <td style={{ fontSize: "1rem" }} className="align-middle text-muted">
                <span title="RS Checks files cannot be restored">—</span>
              </td>
            )}
          </tr>
        )
      }

      <RenameFileModal
        file={file}
        show={showRenameFileModal}
        reloadData={reloadData}
        onHide={() => setShowRenameFileModal(false)} />

      {/* PDF Viewer Modal */}
      {showPdfViewer && (
        <PdfViewer
          uploadedFileId={file?.data?.id}
          show={showPdfViewer}
          onHide={() => setShowPdfViewer(false)}
          title={name || fileName}
          jobService={jobService}
          assignment={null}
          onFlagCreated={reloadData}
          onUploadComplete={exportedFile => {
            setShowPdfViewer(false)
            if (onPdfExport) {
              // Pass normalized file group key so parent can set category options
              const normalizedFileGroup = fileGroupKey || FILE_GROUP_LABEL_TO_KEY[fileGroup] || null
              onPdfExport(exportedFile, normalizedFileGroup)
            }
          }} />
      )}
    </>
  )
}
